import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { UserPlus, UserCheck } from "lucide-react";

/**
 * Reusable Follow toggle for a business or a board.
 *
 * Props:
 *  - targetType: "business" | "board"
 *  - targetId:  the business_id or board_id
 *  - variant:   "default" | "compact"
 *  - showCount: display "X followers" alongside the button
 *
 * Signed-out users get a toast prompting them to sign in — same UX as the
 * favorites/save flows so browsing is never blocked.
 */
export default function FollowButton({
  targetType,
  targetId,
  variant = "default",
  showCount = false,
}) {
  const { user } = useAuth();
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!targetId) return;
    axios
      .get(`${API}/blinterest/follow/state`, {
        params: { target_type: targetType, target_id: targetId },
        withCredentials: true,
      })
      .then((r) => {
        setFollowing(Boolean(r.data.following));
        setFollowers(r.data.followers || 0);
      })
      .catch(() => {});
  }, [targetType, targetId, user]);

  const toggle = async () => {
    if (!user) {
      toast.info("Sign in to follow this " + targetType);
      return;
    }
    if (busy) return;
    setBusy(true);
    const next = !following;
    // Optimistic
    setFollowing(next);
    setFollowers((n) => Math.max(0, n + (next ? 1 : -1)));
    try {
      const opts = { withCredentials: true };
      const body = { target_type: targetType, target_id: targetId };
      if (next) {
        const r = await axios.post(`${API}/blinterest/follow`, body, opts);
        setFollowers(r.data.followers || 0);
      } else {
        const r = await axios.request({
          url: `${API}/blinterest/follow`,
          method: "delete",
          data: body,
          withCredentials: true,
        });
        setFollowers(r.data.followers || 0);
      }
    } catch (err) {
      // revert
      setFollowing(!next);
      setFollowers((n) => Math.max(0, n + (next ? -1 : 1)));
      toast.error(err.response?.data?.detail || "Couldn't update follow");
    } finally {
      setBusy(false);
    }
  };

  const compact = variant === "compact";

  return (
    <div className="inline-flex items-center gap-3">
      <Button
        onClick={toggle}
        disabled={busy}
        variant={following ? "outline" : "default"}
        className={`rounded-full ${
          following
            ? "border-tbc-gold/50 text-tbc-gold bg-transparent hover:bg-tbc-gold/10"
            : "bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy"
        } ${compact ? "h-8 px-3 text-xs" : "h-10 px-5 text-sm"}`}
        data-testid={`follow-btn-${targetType}-${targetId}`}
        data-following={following ? "true" : "false"}
      >
        {following ? (
          <UserCheck className={compact ? "w-3.5 h-3.5 mr-1" : "w-4 h-4 mr-1.5"} />
        ) : (
          <UserPlus className={compact ? "w-3.5 h-3.5 mr-1" : "w-4 h-4 mr-1.5"} />
        )}
        {following ? "Following" : "Follow"}
      </Button>
      {showCount && (
        <span className="text-xs font-body text-white/60" data-testid={`follow-count-${targetType}-${targetId}`}>
          {followers.toLocaleString()} follower{followers === 1 ? "" : "s"}
        </span>
      )}
    </div>
  );
}
